/*
 * Matia — Buy box behaviour (markup: sections/matia-buy.liquid).
 *
 * Loaded once (deferred) on product pages, after matia-core.js and
 * matia-gallery.js. It wires every [data-matia-buy] instance on the page (the
 * main box #matia-buy and the compact #matia-buy2) independently. Per-section
 * data comes from data-attributes and the JSON blob [data-matia-buy-data]:
 * there is zero Liquid in this file.
 *
 * It also owns the "real gifts": $0 gift products are added to / removed from
 * the cart so the cart always matches the picked tier. The cart drawer calls
 * window.MatiaGiftReconcile(cart) on every refresh. Reconciliation is
 * idempotent: it only writes when the wanted gifts differ from the cart.
 *
 * It also tells the cart drawer which bigger pack to offer for the cart's
 * pack (window.MatiaUpgrade(cart)), since only the cards know the packs.
 */
(function () {
  if (window.__matiaBuyInit || !window.Matia) return;
  window.__matiaBuyInit = true;

  var M = window.Matia;

  /* ---------------------------------------------------------------------- */
  /* Gift reconciliation (shared across all buy-box instances)              */
  /* ---------------------------------------------------------------------- */

  var boxData = {}; // sectionId -> parsed [data-matia-buy-data]
  var giftCfg = null; // { mainProductId, gifts[] } merged from every box

  function rebuildGiftConfig() {
    var gifts = [];
    var seen = {};
    var mainProductId = null;

    Object.keys(boxData).forEach(function (id) {
      var d = boxData[id];
      if (d.mainProductId) mainProductId = d.mainProductId;
      (d.gifts || []).forEach(function (g) {
        if (!g || !g.variantId || seen[g.key]) return;
        seen[g.key] = true;
        gifts.push({ key: String(g.key), variantId: String(g.variantId), rank: parseInt(g.rank, 10) || 3 });
      });
    });

    /* Only expose the reconciler when a real gift is configured: otherwise
       the drawer never calls in and the cart is left untouched. */
    giftCfg = gifts.length && mainProductId ? { mainProductId: String(mainProductId), gifts: gifts } : null;
    if (giftCfg) window.MatiaGiftReconcile = reconcile;
    else if (window.MatiaGiftReconcile === reconcile) window.MatiaGiftReconcile = undefined;
  }

  /* Gift lines carry the tier that unlocks them and the product they belong
     to, so the drawer can keep guarding them on pages without a buy box. */
  function addGift(gift, mainProductId) {
    return M.cart.addJson({
      id: gift.variantId,
      quantity: 1,
      properties: {
        _matia_gift: gift.key,
        _matia_gift_rank: String(gift.rank),
        _matia_gift_product: mainProductId
      }
    });
  }

  /* Never lose a gift silently: tell the drawer so the shopper sees why. */
  function notifyGiftError() {
    if (window.MatiaCart && window.MatiaCart.notifyError) {
      window.MatiaCart.notifyError('We could not update your free gifts. Please check your connection and try again.');
    }
  }

  function runInOrder(ops, onError) {
    return ops.reduce(function (p, op) {
      return p.then(function () { return op().catch(onError); });
    }, Promise.resolve());
  }

  /* Resolves to the (possibly updated) cart. It NEVER calls back into the
     drawer, so there is no refresh loop, and issues no write when nothing
     needs changing. */
  function reconcile(cart) {
    var cfg = giftCfg;
    if (!cfg || !cart || !cart.items) return Promise.resolve(cart);

    /* Effective rank = highest rank among the main product's paid lines. No
       main line -> 0 -> every gift removed, also when the shopper deletes
       the main item. */
    var effectiveRank = 0;
    cart.items.forEach(function (it) {
      if (!M.isGift(it) && String(it.product_id) === cfg.mainProductId) {
        effectiveRank = Math.max(effectiveRank, M.rank(it));
      }
    });
    var wanted = {}; // key -> gift, for the gifts this rank unlocks
    cfg.gifts.forEach(function (g) { if (effectiveRank >= g.rank) wanted[g.key] = g; });

    /* No reordering here: /cart.js lists the newest line first, so a gift
       re-added to "move it last" comes back first. The drawer sorts gifts
       last itself (orderedItems in matia-cart.js). */
    var removals = [];
    var kept = {};
    cart.items.forEach(function (it) {
      if (!M.isGift(it)) return;
      var props = it.properties;
      var key = String(props._matia_gift);
      /* A line without our rank/product metadata predates that contract:
         rewrite it so pages without a buy box can guard it too. */
      var stale = props._matia_gift_rank == null
        || (props._matia_gift_product != null && String(props._matia_gift_product) !== cfg.mainProductId);
      /* Remove: not deserved, duplicate, on a selling plan (gifts are always
         one-time) or stale. A deserved gift is re-added clean. */
      if (!wanted[key] || kept[key] || it.selling_plan_allocation || stale) {
        removals.push(function () { return M.cart.change(it.key, 0); });
        return;
      }
      kept[key] = true;
      if (it.quantity !== 1) removals.push(function () { return M.cart.change(it.key, 1); });
    });

    var additions = Object.keys(wanted).filter(function (key) { return !kept[key]; }).map(function (key) {
      return function () { return addGift(wanted[key], cfg.mainProductId); };
    });

    if (!removals.length && !additions.length) return Promise.resolve(cart);

    /* Sequential: line keys stay valid and removals land before re-adds. A
       failed removal is retried on the next reconcile; a failed add is a real
       gift loss, so it is surfaced. Then read the authoritative cart. */
    var addFailed = false;
    return runInOrder(removals, function () {})
      .then(function () { return runInOrder(additions, function () { addFailed = true; }); })
      .then(function () { return M.cart.get().catch(function () { return cart; }); })
      .then(function (finalCart) {
        if (addFailed) notifyGiftError();
        return finalCart;
      });
  }

  /* ---------------------------------------------------------------------- */
  /* Offers                                                                  */
  /* ---------------------------------------------------------------------- */

  /* Everything the page needs to know about a card, read from its radio. */
  function offer(radio) {
    var num = function (name, fallback) { return parseInt(radio.getAttribute(name), 10) || fallback; };
    var charge = num('data-charge', 0);
    return {
      variantId: radio.value,
      charge: charge,
      compare: num('data-compare', 0),
      onetimeCharge: num('data-onetime-charge', charge),
      onetimeLabel: radio.getAttribute('data-onetime-label') || '',
      plan: radio.getAttribute('data-plan') || '',
      note: radio.getAttribute('data-note') || '',
      label: radio.getAttribute('data-label') || '',
      rank: num('data-rank', 1),
      bags: num('data-bags', 1),
      quantity: num('data-quantity', 1),
      referenceUnit: num('data-reference-unit', 0),
      image: radio.getAttribute('data-vimg-src') || ''
    };
  }

  function linePlan(item) {
    var alloc = item.selling_plan_allocation;
    return alloc && alloc.selling_plan ? String(alloc.selling_plan.id) : '';
  }

  /* Cart upgrade banner. When the cart holds exactly one pack of this page's
     product and a bigger card exists, returns the biggest card (1 or 2 bags
     -> 3 bags), what it costs on top, the gifts it adds, and the cart line that
     replaces the current one. Otherwise null. Cards are read from the first
     buy box, live, so the offer always matches the page. */
  function upgrade(cart) {
    var root = document.querySelector('[data-matia-buy][data-matia-buy-ready="1"]');
    var data = root && boxData[root.getAttribute('data-section-id')];
    if (!data || !data.mainProductId || !cart || !cart.items) return null;

    var lines = cart.items.filter(function (it) {
      return !M.isGift(it) && String(it.product_id) === String(data.mainProductId);
    });
    if (lines.length !== 1) return null;
    var line = lines[0];

    var cards = Array.prototype.map.call(root.querySelectorAll('input[name="id"]:not(:disabled)'), offer);
    var fromIndex = -1;
    cards.forEach(function (c, i) {
      if (fromIndex < 0 && c.variantId === String(line.variant_id) && c.quantity === line.quantity && c.plan === linePlan(line)) fromIndex = i;
    });
    if (fromIndex < 0) return null;
    var from = cards[fromIndex];
    var to = null;
    cards.forEach(function (c) {
      if (c.bags > from.bags && (!to || c.bags > to.bags)) to = c;
    });
    if (!to || to.charge <= from.charge) return null;
    /* Price of one bag alone, struck next to the price of the added bags. */
    var single = cards.filter(function (c) { return c.bags === 1; })[0];

    var properties = {
      _matia_compare_at_price: String(to.referenceUnit),
      _matia_cart_image: to.image,
      _matia_rank: String(to.rank)
    };
    var item = { id: to.variantId, quantity: to.quantity, properties: properties };
    if (to.plan) item.selling_plan = to.plan;

    return {
      lineKey: line.key,
      bags: to.bags,
      added: to.bags - from.bags,
      extra: to.charge - from.charge,
      usual: single ? single.charge * (to.bags - from.bags) : 0,
      label: to.label,
      perks: (data.perks || []).filter(function (p) { return p.rank > from.rank && p.rank <= to.rank; }),
      item: item
    };
  }

  window.MatiaUpgrade = upgrade;

  /* ---------------------------------------------------------------------- */
  /* Per-instance wiring                                                     */
  /* ---------------------------------------------------------------------- */

  function initBox(root) {
    if (root.getAttribute('data-matia-buy-ready') === '1') return;
    root.setAttribute('data-matia-buy-ready', '1');

    if (window.MatiaGallery) window.MatiaGallery.init(root);

    var dateEl = root.querySelector('[data-delivery-date]');
    if (dateEl) {
      var days = parseInt(dateEl.getAttribute('data-days'), 10) || 5;
      var date = new Date(Date.now() + days * 86400000);
      dateEl.textContent = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }

    var dataEl = root.querySelector('[data-matia-buy-data]');
    if (!dataEl) return;
    var sectionId = root.getAttribute('data-section-id');
    try { boxData[sectionId] = JSON.parse(dataEl.textContent); } catch (e) { return; }
    rebuildGiftConfig();

    var q = function (sel) { return root.querySelector(sel); };
    var atcNow = q('[data-atc-now]');
    var atcWas = q('[data-atc-was]');
    var atcNote = q('[data-atc-note]');
    var priceNow = q('[data-price-now]');
    var priceWas = q('[data-price-was]');
    var priceBadge = q('[data-price-badge]');
    var priceSave = q('[data-price-save]');
    var onetimeBtn = q('[data-onetime-add]');
    /* The form's hidden inputs, all written here from the selected card. */
    var hidden = {
      plan: q('[data-matia-plan]'),
      quantity: q('[data-matia-quantity]'),
      reference: q('[data-matia-reference-price]'),
      image: q('[data-matia-cart-image]'),
      rank: q('[data-matia-rank]')
    };

    function selected() { return root.querySelector('input[name="id"]:checked'); }

    function setPrice(nowEl, wasEl, o) {
      if (nowEl) nowEl.textContent = M.money(o.charge);
      if (wasEl) {
        wasEl.textContent = M.money(o.compare);
        wasEl.hidden = o.compare <= o.charge;
      }
    }

    function update() {
      var r = selected();
      if (!r) return;
      var o = offer(r);
      var save = Math.max(0, o.compare - o.charge);

      if (hidden.plan) hidden.plan.value = o.plan;
      if (hidden.quantity) hidden.quantity.value = String(o.quantity);
      if (hidden.reference) hidden.reference.value = String(o.referenceUnit);
      if (hidden.image) hidden.image.value = o.image;
      if (hidden.rank) hidden.rank.value = String(o.rank);

      setPrice(atcNow, atcWas, o);
      setPrice(priceNow, priceWas, o);
      if (atcNote) atcNote.textContent = o.note;
      if (priceBadge) priceBadge.hidden = save <= 0;
      if (priceSave) priceSave.textContent = o.compare > 0 ? Math.round((save / o.compare) * 100) + '%' : '';

      /* The one-time link buys the selected card's variant, without its plan. */
      if (onetimeBtn) {
        onetimeBtn.textContent = (onetimeBtn.getAttribute('data-label') || 'One Time Purchase') + ' · ' + M.money(o.onetimeCharge);
        onetimeBtn.offer = o;
      }

      /* Current state on the root, for listeners that load late (sticky bar),
         then the event for the ones already listening. */
      var detail = {
        sectionId: sectionId,
        priceText: M.money(o.charge),
        compareText: o.compare > o.charge ? M.money(o.compare) : '',
        label: o.label,
        rank: o.rank,
        plan: o.plan
      };
      root.dataset.priceText = detail.priceText;
      root.dataset.compareText = detail.compareText;
      root.dataset.label = detail.label;
      root.dataset.rank = detail.rank;
      root.dataset.plan = detail.plan;
      document.dispatchEvent(new CustomEvent('matia:price', { detail: detail }));
    }

    root.querySelectorAll('input[name="id"]').forEach(function (r) {
      r.addEventListener('change', function () {
        update();
      });
    });

    /* Demoted one-time link: adds the selected card's variant without a plan,
       through the drawer, or a plain cart link if the drawer is missing. */
    if (onetimeBtn) {
      onetimeBtn.addEventListener('click', function () {
        var o = onetimeBtn.offer;
        if (!o) return;
        if (!window.MatiaCart) {
          window.location.href = '/cart/add?id=' + encodeURIComponent(o.variantId) + '&quantity=' + o.quantity;
          return;
        }
        var payload = new URLSearchParams();
        payload.append('id', o.variantId);
        payload.append('quantity', String(o.quantity));
        payload.append('properties[_matia_compare_at_price]', String(o.referenceUnit));
        payload.append('properties[_matia_cart_image]', o.image);
        payload.append('properties[_matia_rank]', String(o.rank));
        var label = onetimeBtn.getAttribute('data-label') || '';
        var slideImg = root.querySelector('.m-buy__slide img');
        window.MatiaCart.addPayload(payload, {
          title: onetimeBtn.getAttribute('data-title') || root.getAttribute('data-product-title') || '',
          sub: ((o.onetimeLabel || o.label) ? (o.onetimeLabel || o.label) + ' · ' : '') + label,
          price: M.money(o.onetimeCharge),
          img: o.image || (slideImg ? slideImg.currentSrc || slideImg.src : '')
        });
      });
    }

    update();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-matia-buy]').forEach(initBox);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  /* Theme editor: a re-rendered section is a fresh DOM node, so re-scan. */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
